/** Die Kacheln des Überblicks von "Gesundheit & Körper" (BE-03). Daten kommen aus Training und Ernährung. */
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Circle, CircleDashed, MinusCircle } from 'lucide-react';

import { Erkenntnis, Kennzahl, KennzahlZeile, StatKarte } from '@/ui/composites/Statistik';
import { WOCHENTAGE } from '@/domain/training/typen';
import { kcal, zahl } from '@/modules/ernaehrung/format';
import { ZielBalken } from '@/modules/ernaehrung/Tagesuebersicht';
import { useErnaehrungHeute } from '@/modules/ernaehrung/useErnaehrungHeute';
import { MuskelKarte } from '@/modules/training/koerper/MuskelKarte';
import { zahl1 } from '@/modules/training/format';
import { useTrainingDaten } from '@/modules/training/useTrainingDaten';
import { useTrainingWoche, type TagStatus } from '@/modules/training/useTrainingWoche';
import { formatDe, today } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';

/** Die vier Kennzahlen als Zeile - oben auf der Seite des Bereichs (D-00c). */
export function KennzahlenZeile({ spalten = 4 }: { spalten?: 2 | 3 | 4 }) {
  const heute = today();
  const w = useTrainingWoche(heute);
  const e = useErnaehrungHeute(heute);
  if (!w || !e) return null;
  const { zeile } = e;
  const von = (wert: number | undefined, ziel: number | undefined, einheit: string) => (ziel ? `Ziel ${zahl(ziel, 0)} ${einheit}` : wert === undefined ? 'noch nichts erfasst' : undefined);
  return (
    <KennzahlZeile spalten={spalten}>
        <Kennzahl label="Einheiten diese Woche" wert={`${w.erledigt} / ${w.geplant}`} hinweis={w.ausgefallen ? `${w.ausgefallen} ausgefallen` : w.geplant ? 'laut Plan' : 'kein Plan für diese Woche'} href="/training" />
        <Kennzahl label="Laufkilometer" wert={`${zahl1(w.km)} km`} hinweis="diese Woche" href="/training/laufen" />
        <Kennzahl label="Energie heute" wert={kcal(zeile.summe.werte.kcal)} hinweis={von(zeile.summe.werte.kcal, zeile.ziele.kcal, 'kcal')} href="/ernaehrung" />
        <Kennzahl label="Getrunken" wert={`${zahl(zeile.wasserMl, 0)} ml`} hinweis={von(zeile.wasserMl, zeile.ziele.wasserMl, 'ml')} href="/ernaehrung" />
      </KennzahlZeile>
  );
}

export function KennzahlenKachel() {
  return (
    <StatKarte titel="Kennzahlen" frage="Wie läuft meine Woche und mein Tag?">
      <KennzahlenZeile spalten={2} />
    </StatKarte>
  );
}

const STATUS: Record<TagStatus, { text: string; icon: typeof Circle; farbe: string }> = {
  erledigt: { text: 'erledigt', icon: CheckCircle2, farbe: 'text-ok' },
  teilweise: { text: 'teilweise', icon: MinusCircle, farbe: 'text-warn' },
  ausgefallen: { text: 'ausgefallen', icon: AlertTriangle, farbe: 'text-warn' },
  offen: { text: 'offen', icon: Circle, farbe: 'text-ink-muted' },
  frei: { text: 'frei', icon: CircleDashed, farbe: 'text-ink-faint' },
};

export function WochenplanKachel() {
  const heute = today();
  const w = useTrainingWoche(heute);
  if (!w) return null;
  return (
    <StatKarte titel="Wochenplan" frage="Was steht diese Woche an, und was habe ich geschafft?"
      aktion={<Link to="/training/plan" className="text-[12px] text-accent-text hover:underline">Plan</Link>}>
      <ul className="flex flex-col divide-y divide-line">
        {w.tage.map((t, i) => {
          const s = STATUS[t.status];
          const Icon = s.icon;
          const namen = [...t.einheiten.map((e) => e.einheit.name), ...(t.frei ? [`${t.frei} freie Aktivität${t.frei > 1 ? 'en' : ''}`] : [])];
          return (
            <li key={t.datum} className={cn('flex items-center gap-3 py-1.5 text-sm', t.datum === heute && 'font-medium')} aria-current={t.datum === heute ? 'date' : undefined}>
              <span className="w-12 shrink-0"><span className="block">{WOCHENTAGE[i]!.kurz}</span><span className="block text-[11px] font-normal text-ink-faint tabular-nums">{formatDe(t.datum, 'dd.MM.')}</span></span>
              <span className="min-w-0 flex-1 truncate text-ink-muted">{namen.length ? namen.join(', ') : 'Nichts geplant'}</span>
              <span className={cn('flex shrink-0 items-center gap-1 text-[12px]', s.farbe)}><Icon className="size-4" aria-hidden /> {s.text}</span>
            </li>
          );
        })}
      </ul>
    </StatKarte>
  );
}

export function MuskelnKachel() {
  const d = useTrainingDaten();
  if (!d.bereit) return <StatKarte titel="Muskel-Abdeckung"><p className="text-sm text-ink-faint">Lädt …</p></StatKarte>;
  return (
    <MuskelKarte protokolle={d.protokolle} bibliothek={d.bibliothek} heute={d.heute} einstellungen={d.einstellungen} geplantHeute={d.planHeute.geplant} raenge={d.raenge} koerpergewicht={d.koerpergewicht} />
  );
}

export function NaehrwerteKachel() {
  const e = useErnaehrungHeute(today());
  if (!e) return null;
  const { zeile } = e;
  const w = zeile.summe.werte;
  return (
    <StatKarte titel="Nährwerte heute" frage="Wo stehe ich heute gegen mein Ziel?" aktion={<Link to="/ernaehrung" className="text-[12px] text-accent-text hover:underline">Zur Ernährung</Link>}>
      <ZielBalken k="kcal" label="Energie" wert={w.kcal} ziel={zeile.ziele.kcal} einheit="kcal" />
      <ZielBalken k="eiweiss" label="Eiweiß" wert={w.eiweiss} ziel={zeile.ziele.eiweiss} einheit="g" />
      <ZielBalken k="kh" label="Kohlenhydrate" wert={w.kh} ziel={zeile.ziele.kh} einheit="g" />
      <ZielBalken k="fett" label="Fett" wert={w.fett} ziel={zeile.ziele.fett} einheit="g" />
      <ZielBalken k="wasserMl" label="Trinken" wert={zeile.wasserMl} ziel={zeile.ziele.wasserMl} einheit="ml" />
      {zeile.mahlzeiten === 0 && <Erkenntnis>Heute ist noch nichts erfasst.</Erkenntnis>}
    </StatKarte>
  );
}
